"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";
import { DayPicker } from "react-day-picker";
import { arSA, enUS } from "react-day-picker/locale";
import { CalendarDays, Check, ChevronDown, ChevronUp } from "lucide-react";
import { mergeClassNames } from "./class-names";

export interface SelectProps extends React.AriaAttributes {
  children: React.ReactNode;
  className?: string;
  contentCollisionBoundary?: React.ComponentPropsWithoutRef<
    typeof SelectPrimitive.Content
  >["collisionBoundary"];
  contentSide?: React.ComponentPropsWithoutRef<
    typeof SelectPrimitive.Content
  >["side"];
  defaultValue?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  onValueChange?: (value: string) => void;
  required?: boolean;
  title?: string;
  value?: string;
  variant?: "default" | "seamless";
}

const EMPTY_OPTION_VALUE = "__hisani_empty_option__";

/** The single input shell every Hisani control renders: one border, one
 * height, one focus treatment. Controls add only their own state variants
 * on top so a select, date, text, and file field always line up in a form. */
const controlSurfaceClassName =
  "text-body w-full rounded-md border border-border-default bg-surface px-3 py-2 text-start font-normal text-primary outline-none transition-[border-color,box-shadow] placeholder:text-tertiary focus:border-focus focus:ring-2 focus:ring-focus/20 aria-[invalid=true]:border-danger aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-danger/20";

const controlDisabledClassName =
  "disabled:cursor-not-allowed disabled:bg-[var(--color-surface-sunken)] disabled:text-[var(--color-text-disabled)]";

function getDocumentDirection(): "ltr" | "rtl" {
  return document.documentElement.dir === "rtl" ? "rtl" : "ltr";
}

function subscribeToDocumentDirection(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributeFilter: ["dir"],
    attributes: true,
  });
  return () => observer.disconnect();
}

function getDocumentLocale(): string {
  return document.documentElement.lang || "en";
}

function subscribeToDocumentLocale(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributeFilter: ["lang"],
    attributes: true,
  });
  return () => observer.disconnect();
}

function parseDateValue(value: string | undefined): Date | undefined {
  if (value === undefined || value === "") return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : undefined;
}

function formatDateValue(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeInputValue(
  value: string | number | readonly string[] | undefined,
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value.join("") : String(value);
}

function assignInputRef(
  ref: React.ForwardedRef<HTMLInputElement>,
  node: HTMLInputElement | null,
): void {
  if (typeof ref === "function") {
    ref(node);
  } else if (ref !== null) {
    ref.current = node;
  }
}

function isOptionElement(
  node: React.ReactNode,
): node is React.ReactElement<React.OptionHTMLAttributes<HTMLOptionElement>> {
  return React.isValidElement(node) && node.type === "option";
}

function optionValue(
  option: React.ReactElement<React.OptionHTMLAttributes<HTMLOptionElement>>,
): string {
  return option.props.value === undefined ? "" : String(option.props.value);
}

export function Select({
  "aria-label": ariaLabel,
  children,
  className,
  contentCollisionBoundary,
  contentSide,
  defaultValue,
  disabled,
  id,
  name,
  onValueChange,
  required,
  value,
  variant = "default",
  ...triggerProps
}: SelectProps) {
  const [open, setOpen] = React.useState(false);
  const suppressNextClickOpenRef = React.useRef(false);
  const direction = React.useSyncExternalStore<"ltr" | "rtl">(
    subscribeToDocumentDirection,
    getDocumentDirection,
    () => "ltr",
  );
  const options = React.Children.toArray(children).filter(isOptionElement);
  const placeholder = options.find(
    (option) => optionValue(option) === "" && option.props.disabled,
  );
  const hasSelectableEmptyOption = options.some(
    (option) => optionValue(option) === "" && !option.props.disabled,
  );
  const toInternalValue = (nextValue: string): string =>
    nextValue === "" && hasSelectableEmptyOption
      ? EMPTY_OPTION_VALUE
      : nextValue;

  return (
    <span
      className={mergeClassNames("relative block w-full", className)}
      data-hisani-control="select"
    >
      <SelectPrimitive.Root
        open={open}
        onOpenChange={setOpen}
        dir={direction}
        {...(name === undefined ? {} : { name })}
        {...(required === undefined ? {} : { required })}
        {...(disabled === undefined ? {} : { disabled })}
        {...(value === undefined ? {} : { value: toInternalValue(value) })}
        {...(defaultValue === undefined
          ? {}
          : { defaultValue: toInternalValue(defaultValue) })}
        onValueChange={(nextValue) =>
          onValueChange?.(nextValue === EMPTY_OPTION_VALUE ? "" : nextValue)
        }
      >
        <SelectPrimitive.Trigger
          {...triggerProps}
          onPointerDown={(event) => {
            if (!open) return;
            event.preventDefault();
            suppressNextClickOpenRef.current = true;
            setOpen(false);
          }}
          onClick={(event) => {
            if (!suppressNextClickOpenRef.current) return;
            suppressNextClickOpenRef.current = false;
            event.preventDefault();
          }}
          {...(id === undefined ? {} : { id })}
          {...(ariaLabel === undefined ? {} : { "aria-label": ariaLabel })}
          className={mergeClassNames(
            variant === "default"
              ? controlSurfaceClassName
              : "text-body w-full rounded-md border-0 bg-transparent px-0 py-0 text-start font-normal text-primary outline-none hover:bg-subtle focus-visible:ring-2 focus-visible:ring-focus/20",
            "flex min-h-11 items-center data-[state=open]:relative data-[state=open]:z-[calc(var(--z-index-dropdown)+1)] data-[state=open]:pointer-events-auto data-[disabled]:cursor-not-allowed data-[disabled]:bg-[var(--color-surface-sunken)] data-[disabled]:text-[var(--color-text-disabled)]",
            variant === "default"
              ? "justify-between gap-3"
              : "justify-start gap-1",
          )}
        >
          <SelectPrimitive.Value placeholder={placeholder?.props.children} />
          <SelectPrimitive.Icon asChild>
            <ChevronDown
              className="size-4 shrink-0 text-tertiary"
              aria-hidden="true"
            />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            {...(contentCollisionBoundary === undefined
              ? {}
              : { collisionBoundary: contentCollisionBoundary })}
            {...(contentSide === undefined ? {} : { side: contentSide })}
            sideOffset={4}
            data-hisani-select-content
            {...(ariaLabel === undefined ? {} : { "aria-label": ariaLabel })}
            className="z-[calc(var(--z-index-modal)+1)] max-h-[min(20rem,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-border-subtle bg-surface text-primary shadow-[var(--elevation-floating)]"
          >
            <SelectPrimitive.ScrollUpButton
              className="flex h-11 cursor-default items-center justify-center text-secondary"
              data-hisani-select-scroll-button
            >
              <ChevronUp className="size-4" aria-hidden="true" />
            </SelectPrimitive.ScrollUpButton>
            <SelectPrimitive.Viewport className="p-1">
              {options.map((option) => {
                const rawValue = optionValue(option);
                if (rawValue === "" && option.props.disabled) return null;
                return (
                  <SelectPrimitive.Item
                    key={rawValue}
                    value={rawValue === "" ? EMPTY_OPTION_VALUE : rawValue}
                    {...(option.props.disabled === undefined
                      ? {}
                      : { disabled: option.props.disabled })}
                    className="text-body relative flex min-h-11 cursor-default select-none items-center rounded-md py-2 pe-9 ps-3 outline-none data-[disabled]:pointer-events-none data-[disabled]:text-[var(--color-text-disabled)] data-[highlighted]:bg-brand-subtle data-[highlighted]:text-brand-ink"
                  >
                    <SelectPrimitive.ItemText>
                      {option.props.children}
                    </SelectPrimitive.ItemText>
                    <SelectPrimitive.ItemIndicator className="absolute end-3 inline-flex items-center text-brand-strong">
                      <Check className="size-4" aria-hidden="true" />
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                );
              })}
            </SelectPrimitive.Viewport>
            <SelectPrimitive.ScrollDownButton
              className="flex h-11 cursor-default items-center justify-center text-secondary"
              data-hisani-select-scroll-button
            >
              <ChevronDown className="size-4" aria-hidden="true" />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </span>
  );
}

export interface DateFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "className" | "type"
> {
  className?: string;
  formatDisplayValue?: (value: string) => string;
  onValueChange?: (value: string) => void;
}

export const DateField = React.forwardRef<HTMLInputElement, DateFieldProps>(
  (
    {
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-label": ariaLabel,
      className,
      defaultValue,
      disabled,
      formatDisplayValue,
      id,
      max,
      min,
      onChange,
      onInvalid,
      onValueChange,
      placeholder = "YYYY-MM-DD",
      required,
      title,
      value,
      ...inputProps
    },
    ref,
  ) => {
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const controlledValue = normalizeInputValue(value);
    const initialValue = normalizeInputValue(defaultValue) ?? "";
    const [uncontrolledValue, setUncontrolledValue] =
      React.useState(initialValue);
    const [open, setOpen] = React.useState(false);
    const direction = React.useSyncExternalStore<"ltr" | "rtl">(
      subscribeToDocumentDirection,
      getDocumentDirection,
      () => "ltr",
    );
    const documentLocale = React.useSyncExternalStore(
      subscribeToDocumentLocale,
      getDocumentLocale,
      () => "en",
    );
    const dateValue = controlledValue ?? uncontrolledValue;
    const selectedDate = parseDateValue(dateValue);
    const minimumDate = parseDateValue(normalizeInputValue(min));
    const maximumDate = parseDateValue(normalizeInputValue(max));
    const calendarLocale = documentLocale.startsWith("ar") ? arSA : enUS;
    const displayedValue =
      selectedDate === undefined
        ? placeholder
        : (formatDisplayValue?.(dateValue) ??
          new Intl.DateTimeFormat(documentLocale, {
            dateStyle: "medium",
          }).format(selectedDate));

    React.useEffect(() => {
      const form = triggerRef.current?.form;
      if (
        form === null ||
        form === undefined ||
        controlledValue !== undefined
      ) {
        return;
      }
      const reset = () => setUncontrolledValue(initialValue);
      form.addEventListener("reset", reset);
      return () => form.removeEventListener("reset", reset);
    }, [controlledValue, initialValue]);

    const updateValue = (nextValue: string) => {
      if (controlledValue === undefined) setUncontrolledValue(nextValue);
      onValueChange?.(nextValue);

      const input = inputRef.current;
      if (input !== null && onChange !== undefined) {
        const setter = Object.getOwnPropertyDescriptor(
          HTMLInputElement.prototype,
          "value",
        )?.set;
        if (setter === undefined) input.value = nextValue;
        else setter.call(input, nextValue);
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    };

    return (
      <span
        className={mergeClassNames("relative block w-full", className)}
        data-hisani-control="date"
      >
        <input
          {...inputProps}
          ref={(node) => {
            inputRef.current = node;
            assignInputRef(ref, node);
          }}
          type="date"
          value={dateValue}
          min={min}
          max={max}
          required={required}
          disabled={disabled}
          onChange={(event) => onChange?.(event)}
          onInvalid={(event) => {
            onInvalid?.(event);
            if (!event.defaultPrevented) triggerRef.current?.focus();
          }}
          className="pointer-events-none absolute size-px opacity-0"
          aria-hidden="true"
          tabIndex={-1}
        />
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          <PopoverPrimitive.Trigger asChild>
            <button
              id={id}
              ref={triggerRef}
              type="button"
              disabled={disabled}
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              data-invalid={
                ariaInvalid === true || ariaInvalid === "true" ? "" : undefined
              }
              title={title}
              className={mergeClassNames(
                controlSurfaceClassName,
                controlDisabledClassName,
                "flex min-h-11 items-center justify-between gap-3 data-[invalid]:border-danger data-[invalid]:ring-2 data-[invalid]:ring-danger/20",
              )}
            >
              <span
                className={selectedDate === undefined ? "text-tertiary" : ""}
              >
                {displayedValue}
              </span>
              <CalendarDays
                className="size-4 shrink-0 text-brand-strong"
                aria-hidden="true"
              />
            </button>
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              collisionPadding={8}
              className="z-[calc(var(--z-index-modal)+1)] w-auto max-w-[calc(100vw-1rem)] overflow-auto rounded-lg border border-border-subtle bg-surface p-0 text-primary shadow-[var(--elevation-floating)] outline-none"
            >
              <DayPicker
                mode="single"
                autoFocus
                fixedWeeks
                showOutsideDays
                navLayout="after"
                dir={direction}
                locale={calendarLocale}
                selected={selectedDate}
                defaultMonth={selectedDate ?? minimumDate ?? new Date()}
                {...(minimumDate === undefined
                  ? {}
                  : { startMonth: minimumDate })}
                {...(maximumDate === undefined
                  ? {}
                  : { endMonth: maximumDate })}
                disabled={[
                  ...(minimumDate === undefined
                    ? []
                    : [{ before: minimumDate }]),
                  ...(maximumDate === undefined
                    ? []
                    : [{ after: maximumDate }]),
                ]}
                onSelect={(date) => {
                  if (date === undefined) return;
                  updateValue(formatDateValue(date));
                  setOpen(false);
                }}
                classNames={{
                  root: "relative w-[21rem] max-w-full p-3",
                  months: "relative",
                  month: "w-full",
                  month_caption: "flex h-10 items-center justify-center px-11",
                  caption_label: "text-heading-xsmall",
                  nav: "absolute inset-x-0 top-0 flex justify-between",
                  button_previous:
                    "inline-flex size-11 items-center justify-center rounded-md text-secondary outline-none transition-colors hover:bg-brand-subtle hover:text-brand-ink focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-40",
                  button_next:
                    "inline-flex size-11 items-center justify-center rounded-md text-secondary outline-none transition-colors hover:bg-brand-subtle hover:text-brand-ink focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-40",
                  chevron: "size-4 fill-current",
                  month_grid: "mt-2 w-full border-collapse",
                  weekdays: "border-b border-border-subtle",
                  weekday:
                    "text-body-small h-11 p-0 text-center font-bold text-tertiary",
                  week: "border-0",
                  day: "size-11 p-0 text-center",
                  day_button:
                    "text-body inline-flex size-11 items-center justify-center rounded-md outline-none transition-colors hover:bg-brand-subtle hover:text-brand-ink focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
                  selected:
                    "[&>button]:bg-accent [&>button]:font-bold [&>button]:text-accent-ink [&>button:hover]:bg-accent-hover",
                  today:
                    "[&>button]:border [&>button]:border-brand [&>button]:font-bold",
                  outside: "[&>button]:text-tertiary/45",
                  disabled:
                    "[&>button]:pointer-events-none [&>button]:text-[var(--color-text-disabled)] [&>button]:opacity-50",
                  focused: "[&>button]:ring-2 [&>button]:ring-focus",
                }}
              />
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      </span>
    );
  },
);
DateField.displayName = "DateField";

/** Props a `Field` hands to the control it labels. Spreading them is what
 * associates the visible label, hint, and validation message with the
 * control, so no screen relies on a placeholder or a duplicated
 * `aria-label` to name its input. */
export interface FieldControlProps {
  readonly id: string;
  readonly "aria-describedby": string | undefined;
  readonly "aria-invalid": true | undefined;
}

export interface FieldProps {
  readonly label: React.ReactNode;
  /** Rendered beside the label — for "Optional", a unit, or a format hint.
   * Kept outside the `<label>` so the control's accessible name stays exactly
   * the label text. */
  readonly labelSuffix?: React.ReactNode;
  readonly hint?: React.ReactNode;
  /** Validation message: what happened and how to fix it. */
  readonly error?: React.ReactNode;
  readonly className?: string | undefined;
  readonly children: (control: FieldControlProps) => React.ReactNode;
}

export function Field({
  label,
  labelSuffix,
  hint,
  error,
  className,
  children,
}: FieldProps) {
  const id = React.useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint === undefined ? null : hintId, error === undefined ? null : errorId]
      .filter((value) => value !== null)
      .join(" ") || undefined;

  return (
    <div className={mergeClassNames("grid min-w-0 gap-1.5", className)}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <label htmlFor={id} className="text-body font-semibold text-primary">
          {label}
        </label>
        {labelSuffix === undefined ? null : (
          <span className="text-body-small font-medium text-tertiary">
            {labelSuffix}
          </span>
        )}
      </div>
      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error === undefined ? undefined : true,
      })}
      {hint === undefined ? null : (
        <p id={hintId} className="text-body-small text-secondary">
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p
          id={errorId}
          role="alert"
          className="text-body-small font-semibold text-danger"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export type TextInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> & {
  /** Time and `datetime-local` keep native platform input behavior inside
   * the shared shell; pure dates use the localized `DateField`. */
  readonly type?:
    | "text"
    | "number"
    | "email"
    | "password"
    | "tel"
    | "search"
    | "url"
    | "time"
    | "datetime-local";
};

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ className, type = "text", ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      data-hisani-control="text"
      className={mergeClassNames(
        controlSurfaceClassName,
        controlDisabledClassName,
        "block min-h-11",
        className,
      )}
      {...props}
    />
  ),
);
TextInput.displayName = "TextInput";

export const TextArea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, rows = 3, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    data-hisani-control="textarea"
    className={mergeClassNames(
      controlSurfaceClassName,
      controlDisabledClassName,
      "block min-h-24 resize-y",
      className,
    )}
    {...props}
  />
));
TextArea.displayName = "TextArea";

export type FileFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
>;

export const FileField = React.forwardRef<HTMLInputElement, FileFieldProps>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      type="file"
      data-hisani-control="file"
      className={mergeClassNames(
        "text-body block min-h-11 w-full cursor-pointer rounded-md border border-dashed border-border-default bg-subtle px-3 py-2.5 text-secondary outline-none transition-colors hover:border-border-strong focus-visible:border-focus focus-visible:ring-2 focus-visible:ring-focus/20 disabled:cursor-not-allowed disabled:opacity-60",
        "file:text-body file:me-3 file:min-h-11 file:cursor-pointer file:rounded-md file:border file:border-border-default file:bg-surface file:px-3 file:font-semibold file:text-primary",
        className,
      )}
      {...props}
    />
  ),
);
FileField.displayName = "FileField";

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "children"
> {
  readonly children: React.ReactNode;
  /** Secondary line under the label, for the consequence of the choice. */
  readonly description?: React.ReactNode;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ children, className, description, disabled, ...props }, ref) => (
    <label
      className={mergeClassNames(
        "text-body group flex min-h-11 items-start gap-3 py-2 text-primary",
        disabled === true ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className,
      )}
    >
      <span className="relative mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-sm border border-border-default bg-surface transition-colors has-[input:checked]:border-brand-strong has-[input:checked]:bg-accent">
        <input
          ref={ref}
          type="checkbox"
          disabled={disabled}
          className="peer absolute inset-0 size-full cursor-[inherit] appearance-none rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          {...props}
        />
        <Check
          className="pointer-events-none size-3.5 text-accent-ink opacity-0 transition-opacity peer-checked:opacity-100"
          aria-hidden="true"
          strokeWidth={3}
        />
      </span>
      <span className="min-w-0">
        <span className="block leading-5">{children}</span>
        {description === undefined ? null : (
          <span className="text-body-small mt-0.5 block text-secondary">
            {description}
          </span>
        )}
      </span>
    </label>
  ),
);
Checkbox.displayName = "Checkbox";
